import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import './QuestPanel.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface Quest {
  id: number;
  type: string;
  target: number;
  progress: number;
  completed: boolean;
  reward_claimed: boolean;
  reward_elo: number;
  labels: { tr: { title: string; desc: string }; en: { title: string; desc: string } };
}

const ICONS: Record<string, string> = {
  win_games: '🏆',
  finish_hard: '🔥',
  play_multi: '⚔️',
};

export function QuestPanel() {
  const { token } = useAuth();
  const { t, locale } = useI18n();
  const [quests, setQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API}/quests/today`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data?.quests) setQuests(data.quests);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const claim = async (id: number) => {
    if (!token) return;
    const res = await fetch(`${API}/quests/claim/${id}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) load();
  };

  if (!token || loading || quests.length === 0) return null;

  const qp = t.questPanel as any;
  const title = qp.title;
  const claimLabel = qp.claim;
  const claimedLabel = qp.claimed;

  return (
    <div className="quest-panel">
      <h3 className="quest-panel-title">{title}</h3>
      <div className="quest-list">
        {quests.map(q => {
          // NOT: q.labels backend'den sadece tr/en geliyor (görev başlığı/açıklaması).
          // Diğer locale'lerde EN'e düşer — backend N-dil desteği ayrı bir iş kalemi.
          const lbl = q.labels[locale as 'tr' | 'en'] ?? q.labels.en ?? q.labels.tr;
          const pct = Math.min(100, Math.round((q.progress / q.target) * 100));
          return (
            <div key={q.id} className={`quest-item ${q.completed ? 'is-completed' : ''}`}>
              <span className="quest-icon">{ICONS[q.type] ?? '⭐'}</span>
              <div className="quest-body">
                <div className="quest-head">
                  <strong>{lbl.title}</strong>
                  <span className="quest-progress-text">{q.progress}/{q.target}</span>
                </div>
                <div className="quest-bar">
                  <div className="quest-bar-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="quest-reward">+{q.reward_elo} ELO</div>
              </div>
              {q.completed && (
                <button
                  className="quest-claim"
                  disabled={q.reward_claimed}
                  onClick={() => claim(q.id)}
                >
                  {q.reward_claimed ? claimedLabel : claimLabel}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
